import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getInvoicesPaged, downloadInvoicePDF, getBranches, downloadTailoringReceipt, sendTailoringReceipt, sendInvoiceEmail } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Search, Printer, Eye, FileText, Calendar, Filter, Mail } from 'lucide-react';

const InvoiceList = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [emailModal, setEmailModal] = useState(null); // invoice object
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    branchId: '',
    search: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesData, branchesData] = await Promise.all([
        getInvoicesPaged({}, 1, 25),
        getBranches(),
      ]);
      setInvoices(invoicesData.invoices || []);
      setTotalPages(invoicesData.total_pages || 1);
      setTotalRecords(invoicesData.total || 0);
      setBranches(branchesData);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (targetPage = 1, targetPerPage = perPage) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate)   params.end_date   = filters.endDate;
      if (filters.branchId)  params.branch_id  = filters.branchId;

      const data = await getInvoicesPaged(params, targetPage, targetPerPage);
      setInvoices(data.invoices || []);
      setTotalPages(data.total_pages || 1);
      setTotalRecords(data.total || 0);
      setPage(targetPage);
      setPerPage(targetPerPage);
    } catch {
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setPage(1);
    loadPage(1, perPage);
  };

  const handlePrint = async (invoice) => {
    try {
      toast.info('Generando PDF...');

      // Si es una factura de pedido de confección → comprobante unificado
      if (invoice.tailoring_order_id) {
        const blob = await downloadTailoringReceipt(invoice.tailoring_order_id);
        if (blob.type === 'application/json') {
          const text = await blob.text();
          const json = JSON.parse(text);
          toast.error(json.error || 'Error generando comprobante');
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 3000);
        toast.success('Comprobante de pedido descargado');
        return;
      }

      // Factura normal
      const pdfBlob = await downloadInvoicePDF(invoice.id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al generar PDF');
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo || !emailTo.includes('@')) { toast.error('Ingresa un correo válido'); return; }
    setSendingEmail(true);
    try {
      const invoice = emailModal;
      if (invoice.tailoring_order_id) {
        await sendTailoringReceipt(invoice.tailoring_order_id, emailTo);
        toast.success(`Comprobante de pedido enviado a ${emailTo}`);
      } else {
        toast.info('Envío de facturas normales por correo próximamente');
      }
      setEmailModal(null);
      setEmailTo('');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Error enviando correo');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-DO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        inv.invoice_number?.toLowerCase().includes(search) ||
        inv.ncf?.toLowerCase().includes(search) ||
        inv.customer_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const paymentMethodLabels = {
    efectivo: 'Efectivo',
    tarjeta: 'Tarjeta',
    transferencia: 'Transferencia',
    credito: 'Crédito',
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="invoice-list-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Listado Ventas</h1>
      </div>

      {/* Filtros */}
      <Card className="bg-white border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center" style={{ fontFamily: 'Outfit' }}>
            <Filter className="w-5 h-5 mr-2 text-cyan-500" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="No. factura, NCF, cliente..."
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                data-testid="start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Fin</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                data-testid="end-date"
              />
            </div>
            {user?.role === 'admin' && (
              <div className="space-y-2">
                <Label>Sucursal</Label>
                <Select
                  value={filters.branchId || "all"}
                  onValueChange={(value) => setFilters({ ...filters, branchId: value === "all" ? "" : value })}
                >
                  <SelectTrigger data-testid="branch-filter">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-end">
              <Button
                onClick={handleFilter}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="filter-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtrar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Facturas</p>
            <p className="text-2xl font-bold text-cyan-600">{filteredInvoices.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Ventas</p>
            <p className="text-2xl font-bold text-green-600">
              ${filteredInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Efectivo</p>
            <p className="text-2xl font-bold text-blue-600">
              ${filteredInvoices.filter(i => i.payment_method === 'efectivo').reduce((sum, inv) => sum + (inv.total || 0), 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Crédito</p>
            <p className="text-2xl font-bold text-orange-600">
              ${filteredInvoices.filter(i => i.payment_method === 'credito').reduce((sum, inv) => sum + (inv.total || 0), 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Facturas */}
      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Factura</th>
                  <th className="text-left p-4 font-medium text-slate-600">Tipo</th>
                  <th className="text-left p-4 font-medium text-slate-600">Cliente</th>
                  <th className="text-left p-4 font-medium text-slate-600">Sucursal</th>
                  <th className="text-left p-4 font-medium text-slate-600">Fecha</th>
                  <th className="text-left p-4 font-medium text-slate-600">Método</th>
                  <th className="text-right p-4 font-medium text-slate-600">Total</th>
                  <th className="text-center p-4 font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                      No hay facturas
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <tr 
                      key={invoice.id} 
                      className="border-b hover:bg-slate-50"
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <td className="p-4 font-medium">{invoice.invoice_number}</td>
                      <td className="p-4">
                        {invoice.tailoring_order_id
                          ? <span className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700">Pedido</span>
                          : <span className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600">Venta</span>
                        }
                      </td>
                      <td className="p-4">{invoice.customer_name}</td>
                      <td className="p-4">{invoice.branch_name || '-'}</td>
                      <td className="p-4 text-sm">{formatDate(invoice.created_at)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          invoice.payment_method === 'efectivo' ? 'bg-green-100 text-green-700' :
                          invoice.payment_method === 'credito' ? 'bg-orange-100 text-orange-700' :
                          invoice.payment_method === 'tarjeta' ? 'bg-blue-100 text-blue-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {paymentMethodLabels[invoice.payment_method] || invoice.payment_method}
                        </span>
                      </td>
                      <td className="p-4 text-right font-medium">
                        ${(invoice.total || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedInvoice(invoice)}
                            title="Ver detalles"
                            data-testid={`view-invoice-${invoice.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePrint(invoice)}
                            title="Reimprimir"
                            className="text-cyan-600 hover:text-cyan-700"
                            data-testid={`print-invoice-${invoice.id}`}
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEmailModal(invoice); setEmailTo(''); }}
                            title="Enviar por correo"
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-slate-100">
            {/* Per page selector */}
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>Mostrar</span>
              <select
                value={perPage}
                onChange={(e) => { const v = Number(e.target.value); loadPage(1, v); }}
                className="border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
              >
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>filas &nbsp;·&nbsp; {totalRecords} registros en total</span>
            </div>

            {/* Page navigation */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => loadPage(1, perPage)}
                disabled={page === 1}
                className="px-2 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                title="Primera página"
              >«</button>
              <button
                onClick={() => loadPage(page - 1, perPage)}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
              >‹ Anterior</button>

              <span className="px-4 py-1.5 text-sm text-slate-600">
                Página <strong>{page}</strong> de <strong>{totalPages}</strong>
              </span>

              <button
                onClick={() => loadPage(page + 1, perPage)}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
              >Siguiente ›</button>
              <button
                onClick={() => loadPage(totalPages, perPage)}
                disabled={page >= totalPages}
                className="px-2 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                title="Última página"
              >»</button>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Dialog Enviar por correo */}
      <Dialog open={!!emailModal} onOpenChange={() => setEmailModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Enviar por correo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {emailModal && (
              <p className="text-sm text-slate-500">
                {emailModal.tailoring_order_id
                  ? `Comprobante del pedido — ${emailModal.invoice_number}`
                  : `Factura — ${emailModal?.invoice_number}`}
              </p>
            )}
            <div className="space-y-2">
              <Label>Correo electrónico</Label>
              <Input
                type="email"
                placeholder="cliente@ejemplo.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendEmail()}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEmailModal(null)}>Cancelar</Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Mail className="w-4 h-4 mr-2" />
              {sendingEmail ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Detalles */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              Detalle de Factura {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">NCF</p>
                  <p className="font-mono font-medium">{selectedInvoice.ncf}</p>
                </div>
                <div>
                  <p className="text-slate-500">Fecha</p>
                  <p className="font-medium">{formatDate(selectedInvoice.created_at)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Cliente</p>
                  <p className="font-medium">{selectedInvoice.customer_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Sucursal</p>
                  <p className="font-medium">{selectedInvoice.branch_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Método de Pago</p>
                  <p className="font-medium">{paymentMethodLabels[selectedInvoice.payment_method] || selectedInvoice.payment_method}</p>
                </div>
                <div>
                  <p className="text-slate-500">Vendedor</p>
                  <p className="font-medium">{selectedInvoice.created_by_name || 'N/A'}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="font-medium mb-2">
                  {selectedInvoice.tailoring_order_id ? 'Detalle del abono' : 'Productos'}
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedInvoice.tailoring_order_id ? (
                    // Para pedidos de confección: mostrar línea de abono limpia
                    <div className="bg-slate-50 p-2 rounded text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>Abono al pedido</span>
                        <span className="font-medium">RD${(selectedInvoice.subtotal || 0).toFixed(2)}</span>
                      </div>
                      {selectedInvoice.items?.filter(i => i.product_name?.includes('Confección')).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-slate-500 text-xs pl-2">
                          <span>• {item.product_name}</span>
                        </div>
                      ))}
                      {selectedInvoice.items?.filter(i => !i.product_name?.includes('Confección')).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-slate-500 text-xs pl-2">
                          <span>• {item.quantity}x {item.product_name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Factura normal: mostrar items con subtotales
                    selectedInvoice.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-slate-50 p-2 rounded">
                        <span>
                          {item.quantity}x {item.product_name}
                          {item.size && ` (${item.size}`}
                          {item.color && `/${item.color}`}
                          {item.size && ')'}
                        </span>
                        <span className="font-medium">RD${(item.subtotal || 0).toFixed(2)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="border-t pt-4 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>RD${(selectedInvoice.subtotal || 0).toFixed(2)}</span>
                </div>
                {(selectedInvoice.itbis > 0) && (
                  <div className="flex justify-between">
                    <span>ITBIS (18%)</span>
                    <span>RD${(selectedInvoice.itbis || 0).toFixed(2)}</span>
                  </div>
                )}
                {selectedInvoice.discount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Descuento</span>
                    <span>-RD${(selectedInvoice.discount || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>RD${(selectedInvoice.total || 0).toFixed(2)}</span>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div className="border-t pt-4">
                  <p className="text-slate-500 text-sm">Notas</p>
                  <p className="text-sm">{selectedInvoice.notes}</p>
                </div>
              )}

              <Button
                onClick={() => handlePrint(selectedInvoice)}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="reprint-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Reimprimir Factura
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceList;